<script lang="ts" setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { notificationError } from '@/app/core/util/notificationUtils'
import Popper from 'vue3-popper'
import { useI18n } from 'vue-i18n'
import {
  DBtn,
  DTextField,
  DTable,
  DModal,
  DLoading,
  Edit03Icon,
  Trash03Icon,
  Lock03Icon,
  LockUnlocked03Icon,
  Copy06Icon
} from 'front-library'
import {
  getTemplates,
  deleteTemplate,
  updateStateTemplate,
  copyTemplate
} from '@/app/core/services/receipt-templates'
usePayrollChange(() => window.location.reload())

/* store, router, notifications */
const router = useRouter()
const store = useUserStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const titleModal = ref('')
const descriptionModal = ref()
const acceptQuestion = ref()
const isDelete = ref(false)
const showModalCopy = ref(false)
const titleModalCopy = ref('')
const descriptionModalCopy = ref()
const acceptQuestionCopy = ref()
const loading = ref(false)

/* Datos */
const search = ref()
const tableTemplate = ref(null)
const templateForDelete = ref()
const templateUpdateState = ref()
const copyTemplateId = ref()

/* Datos componente table */
const columns = ref([
  {
    name: 'code',
    title: t('receiptTemplates.list.code'),
    table: 'receipt_templates.code'
  },
  {
    name: 'name',
    title: t('receiptTemplates.list.name'),
    table: 'receipt_templates.name'
  },
  { name: 'companies', title: t('receiptTemplates.list.companies') },
  { name: 'payrolls', title: t('receiptTemplates.list.payrolls') },
  { name: 'processes', title: t('receiptTemplates.list.processes') },
  {
    name: 'is_standard',
    title: t('receiptTemplates.list.standard'),
    table: 'receipt_templates.is_standard'
  },
  {
    name: 'state',
    title: t('receiptTemplates.list.status'),
    table: 'receipt_templates.is_inactive'
  },
  {
    name: 'updated_at',
    title: t('receiptTemplates.list.lastUpdate'),
    table: 'receipt_templates.updated_at'
  },
  { name: 'actions', title: '' }
])
const rows = ref<any[]>([])
const perPage = ref(0)
const offset = ref(0)
const totalRows = ref(0)
const sortBy = ref('')

/* Redirect's */
const createTemplate = () => {
  void router.push({ name: 'CreateReceiptTemplate' })
}
const updateTemplate = (id: any) => {
  void router.push({ name: 'UpdateReceiptTemplate', params: { id } })
}

/* Cambiar estado de la plantilla */
const changeStateTemplate = (values: any) => {
  const text = values.is_inactive
    ? t('table.action.activate')
    : t('table.action.deactivate')
  templateUpdateState.value = [
    { name: values.name, id: values.id, is_inactive: values.is_inactive }
  ]
  showModal.value = true
  titleModal.value = t('receiptTemplates.list.actionStatus', {
    status: text,
    receiptTemplate: values.name as string
  })
  descriptionModal.value = ''
  acceptQuestion.value = t('modal.acceptQuestion', { action: text })
  isDelete.value = false
}
const changeStateConfirm = async () => {
  showModal.value = false
  await updateStateTemplate(
    templateUpdateState.value[0].id,
    !templateUpdateState.value[0].is_inactive
  )
    .then(response => {
      notifications.addNotification({
        title: !templateUpdateState.value[0].is_inactive
          ? t('notification.disabled')
          : t('notification.activated'),
        text: t('receiptTemplates.notification.changeStatus', {
          payrollConcept: templateUpdateState.value[0].name as string,
          status: !templateUpdateState.value[0].is_inactive
            ? t('table.action.deactivated')
            : t('table.action.asset')
        }),
        type: 'success',
        time: 10000
      })
      void templates()
    })
    .catch((error: any) => {
      notificationError(error, 'receiptTemplates', t, notifications, loading)
    })
}

/* Eliminar plantilla */
const removeTemplate = (values: any) => {
  showModal.value = true
  titleModal.value = t('receiptTemplates.list.actionStatus', {
    status: t('table.action.delete'),
    receiptTemplate: values.name as string
  })
  descriptionModal.value = t('modal.description')
  acceptQuestion.value = t('modal.acceptQuestion', {
    action: t('table.action.delete')
  })
  templateForDelete.value = values.id
  isDelete.value = true
}
const deleteConfirm = async () => {
  loading.value = true
  showModal.value = false
  await deleteTemplate(templateForDelete.value)
    .then(response => {
      notifications.addNotification({
        title: t('notification.removed'),
        text: t('receiptTemplates.notification.action', [
          t('notification.wasRemoved')
        ]),
        type: 'success',
        time: 10000
      })
      void templates()
    })
    .catch((error: any) => {
      notificationError(error, 'receiptTemplates', t, notifications, loading)
    })
}

const copyTemplates = (values: any) => {
  showModalCopy.value = true
  titleModalCopy.value = t('receiptTemplates.list.actionStatus', {
    status: t('receiptTemplates.list.copy'),
    receiptTemplate: values.name as string
  })
  descriptionModal.value = t('modal.description')
  acceptQuestion.value = t('modal.acceptQuestion', {
    action: t('receiptTemplates.list.copy')
  })
  copyTemplateId.value = values.id
}
const copyConfirm = async () => {
  showModalCopy.value = false
  await copyTemplate(copyTemplateId.value)
    .then(response => {
      notifications.addNotification({
        title: t('receiptTemplates.notification.copied'),
        text: t('receiptTemplates.notification.action', [
          t('receiptTemplates.notification.copy')
        ]),
        type: 'success',
        time: 10000
      })
      void templates()
    })
    .catch((error: any) => {
      notificationError(error, 'receiptTemplates', t, notifications, loading)
    })
}

/* Cerrar el componente Modal */
const closeModal = () => {
  showModal.value = false
  showModalCopy.value = false
}

/* Listar plantillas */
const templates = async () => {
  loading.value = true
  try {
    const data: any = await getTemplates(
      offset.value,
      perPage.value,
      sortBy.value,
      search.value === '' ? undefined : search.value
    )
    rows.value = await data.results
    totalRows.value = await data.totalItems
    loading.value = false
  } catch (error: any) {
    notificationError(error, 'receiptTemplates', t, notifications, loading)
  }
}
</script>
<template>
  <DLoading v-show="loading" :message="t('loading.data')" />
  <DModal
    :open="showModal"
    typeAlert="question"
    :title="titleModal"
    :description="descriptionModal"
    :acceptQuestion="acceptQuestion"
    @closeModal="closeModal"
    @otherMethod="isDelete ? deleteConfirm() : changeStateConfirm()" />
  <DModal
    :open="showModalCopy"
    typeAlert="question"
    :title="titleModalCopy"
    :description="descriptionModalCopy"
    :acceptQuestion="acceptQuestionCopy"
    @closeModal="closeModal"
    @otherMethod="copyConfirm" />
  <div class="h-full px-5 overflow-auto scroll">
    <div class="bg-gray-50 pb-4">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow"
          v-html="t('receiptTemplates.list.title', 1)" />
        <p
          class="text-gray-500 text-shadow"
          v-html="t('receiptTemplates.list.description')" />
      </div>
      <div class="sm:flex justify-between pt-8">
        <div class="flex mb-3 sm:mb-0 max-w-2xl sm:w-2/3 md:w-1/2 lg:w-5/12">
          <DTextField
            :placeholder="t('form.button.search') + '...'"
            :icon="'SearchLgIcon'"
            class="mr-2 w-full"
            v-model="search"
            @keyup.enter="templates" />
          <DBtn @click.prevent="templates" v-html="t('form.button.search')" />
        </div>
        <DBtn
          :icon="'PlusIcon'"
          type="button"
          @click="createTemplate()"
          v-if="store.hasPermission('receipt_template:create')">
          {{
            t('form.button.create', {
              form: t('receiptTemplates.list.title', 0)
            })
          }}
        </DBtn>
      </div>
    </div>
    <DTable
      ref="tableTemplate"
      :columns="columns"
      :rows="rows"
      :total-rows="totalRows"
      :select-options="false"
      :text-no-data="t('table.noData')"
      :text-per-page="t('table.rowsPerPage')"
      @perPage=";(perPage = $event), templates()"
      @offset="offset = $event"
      @sort=";(sortBy = $event), templates()">
      <template #state="data: any">
        <div
          class="flex items-center rounded-full font-semibold w-fit text-sm py-1 px-3"
          :class="
            data.is_inactive
              ? 'bg-red-100 text-red-600'
              : 'bg-green-100 text-green-600'
          ">
          <div
            class="mr-2 w-1.5 h-1.5 rounded-full"
            :class="data.is_inactive ? 'bg-red-600' : 'bg-green-600'"></div>
          {{
            data.is_inactive
              ? t('table.action.inactive')
              : t('table.action.active')
          }}
        </div>
      </template>
      <template #actions="data: any">
        <div class="flex py-2.5 justify-end gap-1">
          <Popper
            v-if="store.hasPermission('receipt_template:copy')"
            arrow
            offsetDistance="12"
            :content="t('receiptTemplates.list.copyCapitalize')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="copyTemplates(data)">
              <Copy06Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <div>
            <div v-if="data.is_standard">
              <Popper
                v-if="
                  !data.is_inactive &&
                  data.is_standard &&
                  store.hasPermission('receipt_template:update_standard')
                "
                arrow
                offsetDistance="12"
                :content="t('table.action.edit')"
                :hover="true"
                placement="top"
                class="tooltip">
                <div
                  class="bg-gray-50 rounded-md py-2"
                  @click="updateTemplate(data.id)">
                  <Edit03Icon
                    class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
                </div>
              </Popper>
            </div>
            <div v-else>
              <Popper
                v-if="
                  !data.is_inactive &&
                  store.hasPermission('receipt_template:update')
                "
                arrow
                offsetDistance="12"
                :content="t('table.action.edit')"
                :hover="true"
                placement="top"
                class="tooltip">
                <div
                  class="bg-gray-50 rounded-md py-2"
                  @click="updateTemplate(data.id)">
                  <Edit03Icon
                    class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
                </div>
              </Popper>
            </div>
          </div>
          <Popper
            v-if="store.hasPermission('receipt_template:enable')"
            arrow
            offsetDistance="12"
            :content="
              data.is_inactive
                ? t('receiptTemplates.list.activate')
                : t('receiptTemplates.list.deactivate')
            "
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="changeStateTemplate(data)">
              <Lock03Icon
                v-if="data.is_inactive"
                class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
              <LockUnlocked03Icon
                v-else
                class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="
              data.is_inactive && store.hasPermission('receipt_template:delete')
            "
            arrow
            offsetDistance="12"
            :content="t('table.action.remove')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="removeTemplate(data)">
              <Trash03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
        </div>
      </template>
    </DTable>
  </div>
</template>
